import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  BarChart3, FileSpreadsheet, Loader2, AlertCircle, RefreshCcw,
  MapPin, Building2, User, Printer, Search, ChevronRight,
  Tag, Users, Trophy, Receipt,
} from 'lucide-react';
import Button from '../../components/UI/Button';
import { sdk } from '../../lib/sdk';
import { useAuthStore } from '../../stores/authStore';
import { useRegions, useOffices, useDonationTypes } from '../../hooks/useQueries';
import { getErrorMessage } from '../../lib/utils/errorMessage';

import DateRangePicker from './components/DateRangePicker';
import LayoutConfigurator, { LayoutConfig, DEFAULT_LAYOUT } from './components/LayoutConfigurator';
import ReportTable from './components/ReportTable';
import ReportSummary from './components/ReportSummary';
import ReportHeader from './components/ReportHeader';
import { type ReportCashier } from './components/CashierFilter';
import OfficeRegionFilter from './components/OfficeRegionFilter';
import { ReportCategoryBackButton } from './ReportCategorySelector';
import { getApiBaseUrl } from '../../lib/apiBaseUrl';

// ─── Types ───────────────────────────────────────────────
type ReportTab =
  | 'regions'
  | 'offices'
  | 'types'
  | 'typesRegional'
  | 'cashiers'
  | 'topDonors'
  | 'transactions'
  | 'donor';

interface CommonFilters {
  startDate: string;
  endDate: string;
  donationTypeIds: string[];
  cashierIds: string[];
}

interface DonorLookupInfo {
  donorId: string;
  name: string;
  fatherOrHusbandName: string;
  relation: string;
  cnic: string;
  office?: { officeNo: number; name: string };
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgo = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtCurrency = (v: number) =>
  `Rs. ${(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// ─── Tab Definition ───────────────────────────
const TABS: { id: ReportTab; label: string; description: string; icon: React.ReactNode }[] = [
  { id: 'regions', label: 'By Region', description: 'Region-wise totals', icon: <MapPin className="h-4 w-4" /> },
  { id: 'offices', label: 'By Office', description: 'Office-wise totals', icon: <Building2 className="h-4 w-4" /> },
  { id: 'types', label: 'By Fund Type', description: 'Per donation type', icon: <Tag className="h-4 w-4" /> },
  { id: 'typesRegional', label: 'Fund × Region', description: 'Regional fund matrix', icon: <MapPin className="h-4 w-4" /> },
  { id: 'cashiers', label: 'By Cashier', description: 'User performance', icon: <Users className="h-4 w-4" /> },
  { id: 'topDonors', label: 'Top Donors', description: 'Donor leaderboard', icon: <Trophy className="h-4 w-4" /> },
  { id: 'transactions', label: 'Transactions', description: 'Raw donation list', icon: <Receipt className="h-4 w-4" /> },
  { id: 'donor', label: 'Single Donor', description: 'Individual report', icon: <User className="h-4 w-4" /> },
];

// ─── Tab Button ────────────────────────────
const TabButton: React.FC<{
  active: boolean; icon: React.ReactNode; label: string; description: string; onClick: () => void;
}> = ({ active, icon, label, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 transition-all text-left w-full ${
      active
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md'
        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
    }`}
  >
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
      active ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
    }`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className={`text-xs font-semibold ${active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200'}`}>
        {label}
      </p>
      <p className="text-[10px] truncate text-gray-400">{description}</p>
    </div>
    {active && <ChevronRight className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />}
  </button>
);

const MultiSelect: React.FC<{
  label: string;
  options: { id: string; name: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
        {options.length === 0 && <span className="text-xs text-gray-400">No options</span>}
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.id)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              selected.includes(o.id)
                ? 'bg-primary-100 border-primary-400 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
            }`}
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────
const DonationReportsPanel: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
  useAuthStore();
  const printRef = useRef<HTMLDivElement>(null);

  // ── Cached reference data ──
  const { data: regionsResp } = useRegions();
  const { data: officesResp } = useOffices();
  const { data: donationTypesRaw = [] } = useDonationTypes();
  const regionsData: any[] = (regionsResp as any)?.data || [];
  const officesData: any[] = (officesResp as any)?.data || [];
  const donationTypes: any[] = (donationTypesRaw as any[]).filter((t: any) => t.isActive !== false);

  // ── Cashiers list (loaded once) ──
  const [cashiers, setCashiers] = useState<ReportCashier[]>([]);
  const [cashiersLoading, setCashiersLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setCashiersLoading(true);
    sdk.client.get('/api/v1/reports/advanced/cashiers')
      .then((resp: any) => {
        if (!mounted) return;
        if (resp.success && Array.isArray(resp.data)) setCashiers(resp.data);
      })
      .catch(() => {
        // Silently fail; filter just won't have any options
      })
      .finally(() => { if (mounted) setCashiersLoading(false); });
    return () => { mounted = false; };
  }, []);

  // ── State ──
  const [activeTab, setActiveTab] = useState<ReportTab>('regions');
  const [layout, setLayout] = useState<LayoutConfig>(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>(null);

  const [common, setCommon] = useState<CommonFilters>({
    startDate: daysAgo(30), endDate: today(),
    donationTypeIds: [], cashierIds: [],
  });

  // ── Region tab state ──
  const [regionMode, setRegionMode] = useState<'all' | 'selected'>('all');
  const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);

  // ── Office tab state ──
  const [officeMode, setOfficeMode] = useState<'all' | 'selected' | 'byRegion'>('all');
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>([]);
  const [officeFilterRegionId, setOfficeFilterRegionId] = useState('');

  // ── Shared scope filter for new tabs (types, cashiers, topDonors, transactions) ──
  const [scopeRegionIds, setScopeRegionIds] = useState<string[]>([]);
  const [scopeOfficeIds, setScopeOfficeIds] = useState<string[]>([]);
  const [fullOffices, setFullOffices] = useState(false);

  // ── Top donors limit ──
  const [topDonorsLimit, setTopDonorsLimit] = useState<number>(50);

  // ── Transactions pagination ──
  const [txnLimit, setTxnLimit] = useState<number>(200);
  const [txnPage, setTxnPage] = useState<number>(1);

  // ── Donor tab state ──
  const [donorIdInput, setDonorIdInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookedUpDonor, setLookedUpDonor] = useState<DonorLookupInfo | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const switchTab = (tab: ReportTab) => {
    setActiveTab(tab);
    setReportData(null);
    setError(null);
  };

  // ── Build query params ──
  const buildParams = useCallback((): Record<string, string> => {
    const p: Record<string, string> = {
      startDate: common.startDate,
      endDate: common.endDate,
    };
    if (common.donationTypeIds.length > 0) p.donationTypeIds = common.donationTypeIds.join(',');
    if (common.cashierIds.length > 0) p.cashierIds = common.cashierIds.join(',');

    if (activeTab === 'regions') {
      if (regionMode === 'all') {
        p.scope = 'allRegions';
      } else {
        p.scope = selectedRegionIds.length === 1 ? 'singleRegion' : 'multiRegion';
        if (selectedRegionIds.length === 1) p.regionId = selectedRegionIds[0];
        else p.regionIds = selectedRegionIds.join(',');
      }
    }

    if (activeTab === 'offices') {
      if (officeMode === 'all' || officeMode === 'byRegion') {
        p.scope = 'officeComparison';
        if (officeMode === 'byRegion' && officeFilterRegionId) p.regionIds = officeFilterRegionId;
      } else {
        if (selectedOfficeIds.length === 1) {
          p.scope = 'singleOffice';
          p.officeId = selectedOfficeIds[0];
        } else {
          p.scope = 'officeComparison';
          p.officeIds = selectedOfficeIds.join(',');
        }
      }
    }

    if (activeTab === 'types') {
      p.scope = 'byDonationType';
      if (scopeRegionIds.length > 0) p.regionIds = scopeRegionIds.join(',');
      if (scopeOfficeIds.length > 0) p.officeIds = scopeOfficeIds.join(',');
      if (fullOffices) p.fullOffices = 'true';
    }

    if (activeTab === 'typesRegional') {
      p.scope = 'byDonationTypeByRegion';
      if (scopeRegionIds.length > 0) p.regionIds = scopeRegionIds.join(',');
      if (scopeOfficeIds.length > 0) p.officeIds = scopeOfficeIds.join(',');
    }

    if (activeTab === 'cashiers') {
      p.scope = 'byCashier';
      if (scopeRegionIds.length > 0) p.regionIds = scopeRegionIds.join(',');
      if (scopeOfficeIds.length > 0) p.officeIds = scopeOfficeIds.join(',');
    }

    if (activeTab === 'topDonors') {
      p.scope = 'topDonors';
      p.limit = String(topDonorsLimit);
      if (scopeRegionIds.length > 0) p.regionIds = scopeRegionIds.join(',');
      if (scopeOfficeIds.length > 0) p.officeIds = scopeOfficeIds.join(',');
    }

    if (activeTab === 'transactions') {
      p.scope = 'transactions';
      p.limit = String(txnLimit);
      p.page = String(txnPage);
      if (scopeRegionIds.length > 0) p.regionIds = scopeRegionIds.join(',');
      if (scopeOfficeIds.length > 0) p.officeIds = scopeOfficeIds.join(',');
    }

    if (activeTab === 'donor') {
      p.scope = 'singleDonor';
      p.donorId = lookedUpDonor?.donorId || donorIdInput.toUpperCase();
    }

    return p;
  }, [
    activeTab, common, regionMode, selectedRegionIds,
    officeMode, selectedOfficeIds, officeFilterRegionId,
    scopeRegionIds, scopeOfficeIds, fullOffices,
    topDonorsLimit, txnLimit, txnPage,
    lookedUpDonor, donorIdInput,
  ]);

  // ── Donor lookup ──
  const handleDonorLookup = async () => {
    const id = donorIdInput.trim().toUpperCase();
    if (!id) { setLookupError('Please enter a Donor ID'); return; }
    setLookupLoading(true); setLookupError(null); setLookedUpDonor(null);
    try {
      const resp = await sdk.client.get(`/api/v1/donors/by-donor-id/${encodeURIComponent(id)}`);
      if (resp.success && resp.data) {
        setLookedUpDonor(resp.data);
      } else {
        setLookupError(resp.message || `No donor found with ID "${id}"`);
      }
    } catch (e: any) {
      setLookupError(getErrorMessage(e, { fallback: 'Lookup failed. Please try again.', notFound: `No donor found with ID "${id}".` }));
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Generate report ──
  const handleGenerate = async () => {
    setError(null);
    if (!common.startDate || !common.endDate) { setError('Please select a date range'); return; }
    if (activeTab === 'regions' && regionMode === 'selected' && selectedRegionIds.length === 0) { setError('Please select at least one region'); return; }
    if (activeTab === 'offices' && officeMode === 'selected' && selectedOfficeIds.length === 0) { setError('Please select at least one office'); return; }
    if (activeTab === 'donor' && !lookedUpDonor) { setError('Please look up a donor first'); return; }

    setLoading(true); setReportData(null);
    try {
      const qs = new URLSearchParams(buildParams()).toString();
      const response = await sdk.client.get(`/api/v1/reports/advanced/generate?${qs}`);
      if (response.success) { setReportData(response.data); }
      else { setError(response.message || 'Failed to generate report'); }
    } catch (e: any) { setError(getErrorMessage(e, 'Generation failed')); }
    finally { setLoading(false); }
  };

  // ── Print ──
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { alert('Please allow popups to print.'); return; }

    const fontBase = layout.fontSize === 'small' ? '10px' : layout.fontSize === 'large' ? '14px' : '12px';
    const fontSmall = layout.fontSize === 'small' ? '8px' : layout.fontSize === 'large' ? '11px' : '10px';
    const pageSize = layout.paperSize || 'A4';
    const orientation = layout.pageOrientation || 'landscape';

    win.document.write(`<!DOCTYPE html><html><head><title>Report</title>
      <style>
        :root {
          --report-font-base: ${fontBase};
          --report-font-small: ${fontSmall};
          --report-primary: #1a5276;
        }
        @page { size: ${pageSize} ${orientation}; margin: 12mm; }
        body { margin: 0; padding: 16px; font-family: Arial, Helvetica, sans-serif;
          font-size: var(--report-font-base); color: #111; }
        .report-print-root { font-size: var(--report-font-base); }
        table { border-collapse: collapse; width: 100%; font-size: var(--report-font-base); }
        th, td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; }
        th { background: var(--report-primary); color: #fff; font-size: var(--report-font-small); }
        tr:nth-child(even) { background: #f0f4f8; }
        .print-section, .section-header, .my-5 { break-inside: avoid; page-break-inside: avoid; }
        .print-letterhead { break-after: avoid; page-break-after: avoid; }
        thead { display: table-header-group; }
        .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; margin-bottom: 16px; }
        @media print {
          body { margin: 0; padding: 8px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style></head><body>`);
    win.document.write(`<div class="report-print-root" data-font-size="${layout.fontSize}" data-orientation="${orientation}">`);
    win.document.write(printContent.innerHTML);
    win.document.write('</div></body></html>');
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  };

  // ── Export Excel ──
  const handleExportExcel = async () => {
    if (!reportData) { setError('Generate a report first'); return; }
    setExporting(true);
    try {
      const token = window.localStorage.getItem('hria-token');
      const API = getApiBaseUrl();
      const body = { ...buildParams(), format: 'excel', layoutConfig: layout };
      const resp = await fetch(`${API}/api/v1/reports/advanced/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(e.message);
      }
      const blob = await resp.blob();
      const dateStr = today();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `report_${activeTab}_${dateStr}.xlsx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e: any) { setError(getErrorMessage(e, 'Export failed')); }
    finally { setExporting(false); }
  };

  // ── Export PDF (server-rendered, professional) ──
  const handleExportPdf = async () => {
    if (!reportData) { setError('Generate a report first'); return; }
    setExporting(true);
    try {
      const token = window.localStorage.getItem('hria-token');
      const API = getApiBaseUrl();
      const body = { ...buildParams(), format: 'pdf', layoutConfig: layout };
      const resp = await fetch(`${API}/api/v1/reports/advanced/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(e.message);
      }
      const blob = await resp.blob();
      const dateStr = today();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `report_${activeTab}_${dateStr}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch (e: any) { setError(getErrorMessage(e, 'PDF export failed')); }
    finally { setExporting(false); }
  };

  // ── Helpers ──
  const toggleItem = (id: string, arr: string[], setArr: (a: string[]) => void) => {
    setArr(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  };

  // ── Preview builders ──

  const renderRegionOfficePreview = () => {
    const typeNames: string[] = reportData.header?.donationTypes || [];
    const isSingle = typeNames.length === 1;
    const rt = reportData.reportType;

    const summaryItems: any[] = [];
    if (reportData.summary?.totalRegions !== undefined) summaryItems.push({ label: 'Regions', value: reportData.summary.totalRegions });
    if (reportData.summary?.totalOffices !== undefined) summaryItems.push({ label: 'Offices', value: reportData.summary.totalOffices });
    if (reportData.summary?.totalDonors !== undefined) summaryItems.push({ label: 'Donors', value: reportData.summary.totalDonors });
    summaryItems.push({ label: 'Total Donations', value: reportData.summary?.totalDonations || 0 });
    summaryItems.push({ label: 'Grand Total', value: fmtCurrency(reportData.summary?.grandTotal || 0), color: 'text-emerald-600 dark:text-emerald-400' });

    const isRegion = rt === 'allRegions' || rt === 'multiRegion';
    const isOffice = rt === 'singleRegion' || rt === 'officeComparison';
    const isComparison = rt === 'officeComparison';
    let columns: any[];
    let flatRows: any[];

    if (isSingle) {
      columns = isRegion
        ? [{ label: 'Region #', key: 'regionNo' }, { label: 'Region Name', key: 'regionName' }, { label: 'Controller', key: 'controllerName' }, { label: 'Donations', key: 'donationCount', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }]
        : isOffice
          ? [
            ...(isComparison ? [{ label: 'Rank', key: 'rank', align: 'right' as const }] : []),
            { label: 'Office #', key: 'officeNo' },
            { label: 'Office Name', key: 'officeName' },
            ...(isComparison ? [{ label: 'Region', key: 'regionName' }] : [{ label: 'Controller', key: 'controllerName' }]),
            { label: 'Donations', key: 'donationCount', align: 'right' },
            { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' },
            ...(isComparison ? [{ label: '% Share', key: 'percentShareLabel', align: 'right' as const }] : []),
          ]
          : [{ label: 'Donor ID', key: 'donorId' }, { label: 'Name', key: 'donorName' }, { label: 'Father/Husband', key: 'fatherOrHusbandName' }, { label: 'Donations', key: 'donationCount', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }];

      flatRows = (reportData.sections || []).map((s: any) => {
        const r = s.rows?.[0] || { donationCount: 0, totalAmount: 0 };
        return {
          ...s,
          donationCount: r.donationCount,
          totalAmount: r.totalAmount,
          percentShareLabel: s.percentShare != null ? `${s.percentShare}%` : '—',
        };
      });
    } else {
      columns = isRegion
        ? [{ label: 'Region #', key: 'regionNo' }, { label: 'Region', key: 'regionName' }, { label: 'Controller', key: 'controllerName' }, { label: 'Fund Type', key: 'donationType' }, { label: 'Count', key: 'donationCount', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }]
        : isOffice
          ? [
            ...(isComparison ? [{ label: 'Rank', key: 'rank', align: 'right' as const }] : []),
            { label: 'Office #', key: 'officeNo' },
            { label: 'Office', key: 'officeName' },
            ...(isComparison ? [{ label: 'Region', key: 'regionName' }] : [{ label: 'Controller', key: 'controllerName' }]),
            { label: 'Fund Type', key: 'donationType' },
            { label: 'Count', key: 'donationCount', align: 'right' },
            { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' },
            ...(isComparison ? [{ label: '% Share', key: 'percentShareLabel', align: 'right' as const }] : []),
          ]
          : [{ label: 'Donor ID', key: 'donorId' }, { label: 'Name', key: 'donorName' }, { label: 'Father/Husband', key: 'fatherOrHusbandName' }, { label: 'Fund Type', key: 'donationType' }, { label: 'Count', key: 'donationCount', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }];

      flatRows = [];
      for (const section of (reportData.sections || [])) {
        const shareLabel = section.percentShare != null ? `${section.percentShare}%` : '—';
        if (!section.rows?.length) {
          flatRows.push({
            ...section,
            donationType: '—',
            donationCount: 0,
            totalAmount: 0,
            percentShareLabel: shareLabel,
          });
        } else {
          for (const row of section.rows) {
            flatRows.push({
              ...section,
              donationType: row.donationType,
              donationCount: row.donationCount,
              totalAmount: row.totalAmount,
              percentShareLabel: shareLabel,
            });
          }
        }
      }
    }

    return (
      <>
        <ReportSummary items={summaryItems} />
        <div className={isComparison ? 'overflow-x-auto' : undefined}>
          <ReportTable
            columns={columns} rows={flatRows} showTotals
            sectionTitle={isSingle ? `Donations — ${typeNames[0] || 'All Types'}` : 'Donations by Fund Type'}
            colorScheme={layout.colorScheme}
          />
        </div>
      </>
    );
  };

  const renderDonorPreview = () => {
    const summaryItems = [
      { label: 'Total Donations', value: reportData.summary?.totalDonations || 0 },
      { label: 'Total Amount', value: fmtCurrency(reportData.summary?.totalAmount || 0), color: 'text-emerald-600 dark:text-emerald-400' },
      { label: 'Average', value: fmtCurrency(reportData.summary?.averageDonation || 0) },
      { label: 'Lifetime Total', value: fmtCurrency(reportData.summary?.lifetime?.totalAmount || 0), color: 'text-purple-600 dark:text-purple-400' },
    ];
    const typeCols = [
      { label: 'Donation Type', key: 'donationType' },
      { label: 'Count', key: 'donationCount', align: 'right' as const },
      { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' as const },
    ];
    const txCols = [
      { label: 'Receipt #', key: 'receiptId' },
      { label: 'Date', key: 'dateStr' },
      { label: 'Type', key: 'donationType' },
      { label: 'Amount', key: 'amount', isCurrency: true, align: 'right' as const },
      { label: 'Office', key: 'officeName' },
      { label: 'Cashier', key: 'cashierName' },
    ];
    const txRows = (reportData.transactions || []).map((t: any) => ({
      ...t, dateStr: new Date(t.date).toLocaleDateString('en-GB'),
    }));

    return (
      <>
        <ReportSummary items={summaryItems} />
        {reportData.donorInfo && (
          <div className="my-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Donor Information</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-sm">
              <div><span className="text-gray-400 text-xs">ID</span><p className="font-medium">{reportData.donorInfo.donorId}</p></div>
              <div><span className="text-gray-400 text-xs">Name</span><p className="font-medium">{reportData.donorInfo.name}</p></div>
              <div><span className="text-gray-400 text-xs">{reportData.donorInfo.relation}</span><p className="font-medium">{reportData.donorInfo.fatherOrHusbandName}</p></div>
              <div><span className="text-gray-400 text-xs">CNIC</span><p className="font-medium font-mono">{reportData.donorInfo.cnic}</p></div>
              {reportData.donorInfo.office && (
                <div><span className="text-gray-400 text-xs">Office</span><p className="font-medium">#{reportData.donorInfo.office.officeNo} — {reportData.donorInfo.office.name}</p></div>
              )}
            </div>
          </div>
        )}
        {reportData.summary?.typeBreakdown?.length > 0 && (
          <ReportTable columns={typeCols} rows={reportData.summary.typeBreakdown} showTotals sectionTitle="By Donation Type" colorScheme={layout.colorScheme} />
        )}
        {txRows.length > 0 && (
          <ReportTable columns={txCols} rows={txRows} showTotals sectionTitle={`All Transactions (${txRows.length})`} colorScheme={layout.colorScheme} />
        )}
      </>
    );
  };

  const renderRegionalTypePreview = () => {
    const sections = (reportData.sections || []) as any[];
    const summaryItems = [
      { label: 'Regions', value: reportData.summary?.totalRegions || 0 },
      { label: 'Total Donations', value: reportData.summary?.totalDonations || 0 },
      { label: 'Grand Total', value: fmtCurrency(reportData.summary?.grandTotal || 0), color: 'text-emerald-600 dark:text-emerald-400' },
    ];
    return (
      <>
        <ReportSummary items={summaryItems} />
        {sections.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-6">No regional activity in this period.</p>
        )}
        {sections.map((sec: any) => (
          <div key={`${sec.regionNo}-${sec.regionName}`} className="my-5 print-section">
            <div className="flex items-center justify-between gap-3 mb-2 px-3 py-2 bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 rounded">
              <div>
                <p className="text-sm font-bold text-primary-700 dark:text-primary-300">
                  R-{sec.regionNo} — {sec.regionName}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{sec.controllerName}</p>
              </div>
              <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400 font-mono tabular-nums">
                {fmtCurrency(sec.sectionTotal)}
              </p>
            </div>
            {(sec.rows || []).length > 0 ? (
              <ReportTable
                columns={[
                  { label: 'Fund Type', key: 'donationType' },
                  { label: 'Count', key: 'donationCount', align: 'right' as const },
                  { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' as const },
                  { label: '% Region', key: 'percentOfRegion', align: 'right' as const },
                ]}
                rows={(sec.rows || []).map((r: any) => ({
                  ...r,
                  percentOfRegion: r.percentOfRegion != null ? `${r.percentOfRegion}%` : '—',
                }))}
                showTotals
                totalsLabel="REGION TOTAL"
                colorScheme={layout.colorScheme}
              />
            ) : (
              <p className="text-xs text-gray-400 px-3 py-2">No donations in this region.</p>
            )}
          </div>
        ))}
      </>
    );
  };

  const renderTypePreview = () => {
    const sections = (reportData.sections || []) as any[];
    const limited = reportData.meta?.officesLimited === true;
    const summaryItems = [
      { label: 'Fund Types', value: reportData.summary?.totalTypes || 0 },
      { label: 'Total Donations', value: reportData.summary?.totalDonations || 0 },
      { label: 'Grand Total', value: fmtCurrency(reportData.summary?.grandTotal || 0), color: 'text-emerald-600 dark:text-emerald-400' },
    ];
    return (
      <>
        <ReportSummary items={summaryItems} />
        {limited && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-3 px-1">
            Showing top 25 offices per fund type. Enable &quot;All offices&quot; in filters for the full list.
          </p>
        )}
        {sections.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-6">No fund types had activity in this period.</p>
        )}
        {sections.map((sec: any) => (
          <div key={sec.donationTypeId} className="my-5 print-section">
            <div className="flex items-center justify-between gap-3 mb-2 px-3 py-2 bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 rounded">
              <div>
                <p className="text-sm font-bold text-primary-700 dark:text-primary-300">{sec.donationType}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">{(sec.donationCount || 0).toLocaleString()} donations</p>
              </div>
              <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400 font-mono tabular-nums">
                {fmtCurrency(sec.totalAmount)}
              </p>
            </div>
            {(sec.topOffices || []).length > 0 ? (
              <ReportTable
                columns={[
                  { label: 'Office #', key: 'officeNo' },
                  { label: 'Office', key: 'officeName' },
                  { label: 'Region', key: 'regionName' },
                  { label: 'Donations', key: 'count', align: 'right' as const },
                  { label: 'Amount', key: 'amount', isCurrency: true, align: 'right' as const },
                ]}
                rows={sec.topOffices}
                showTotals totalsLabel="SUBTOTAL"
                colorScheme={layout.colorScheme}
              />
            ) : (
              <p className="text-xs text-gray-400 px-3 py-2">No office breakdown available.</p>
            )}
          </div>
        ))}
      </>
    );
  };

  const renderCashierPreview = () => {
    const sections = (reportData.sections || []) as any[];
    const summaryItems = [
      { label: 'Cashiers', value: reportData.summary?.totalCashiers || 0 },
      { label: 'Total Donations', value: reportData.summary?.totalDonations || 0 },
      { label: 'Grand Total', value: fmtCurrency(reportData.summary?.grandTotal || 0), color: 'text-emerald-600 dark:text-emerald-400' },
    ];
    return (
      <>
        <ReportSummary items={summaryItems} />
        {sections.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-6">No cashier activity in this period.</p>
        )}
        {sections.map((sec: any) => (
          <div key={sec.cashierId} className="my-5">
            <div className="flex items-center justify-between gap-3 mb-2 px-3 py-2 bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 rounded">
              <div className="min-w-0">
                <p className="text-sm font-bold text-primary-700 dark:text-primary-300 truncate">
                  {sec.cashierName} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({sec.cashierRole})</span>
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {sec.cashierOffice} · {(sec.sectionCount || 0).toLocaleString()} donations
                </p>
              </div>
              <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400 font-mono tabular-nums whitespace-nowrap">
                {fmtCurrency(sec.sectionTotal)}
              </p>
            </div>
            {(sec.rows || []).length > 0 ? (
              <ReportTable
                columns={[
                  { label: 'Donation Type', key: 'donationType' },
                  { label: 'Count', key: 'donationCount', align: 'right' as const },
                  { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' as const },
                ]}
                rows={sec.rows}
                showTotals totalsLabel="SUBTOTAL"
                colorScheme={layout.colorScheme}
              />
            ) : (
              <p className="text-xs text-gray-400 px-3 py-2">No type breakdown available.</p>
            )}
          </div>
        ))}
      </>
    );
  };

  const renderTopDonorsPreview = () => {
    const rows = ((reportData.rows || []) as any[]).map((r, idx) => ({
      ...r,
      rank: idx + 1,
      officeLabel: r.officeNo ? `#${r.officeNo} — ${r.officeName || ''}` : (r.officeName || '—'),
      lastDonationStr: r.lastDonation ? new Date(r.lastDonation).toLocaleDateString('en-GB') : '—',
    }));
    const summaryItems = [
      { label: 'Donors', value: reportData.summary?.totalDonors || 0 },
      { label: 'Total Donations', value: reportData.summary?.totalDonations || 0 },
      { label: 'Grand Total', value: fmtCurrency(reportData.summary?.grandTotal || 0), color: 'text-emerald-600 dark:text-emerald-400' },
      { label: 'Avg / Donor', value: fmtCurrency(reportData.summary?.averagePerDonor || 0) },
    ];
    return (
      <>
        <ReportSummary items={summaryItems} />
        <ReportTable
          columns={[
            { label: '#', key: 'rank', align: 'right' as const },
            { label: 'Donor ID', key: 'donorId' },
            { label: 'Donor Name', key: 'donorName' },
            { label: 'Father/Husband', key: 'fatherOrHusbandName' },
            { label: 'Office', key: 'officeLabel' },
            { label: 'Last Donation', key: 'lastDonationStr' },
            { label: 'Donations', key: 'donationCount', align: 'right' as const },
            { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' as const },
          ]}
          rows={rows}
          showTotals totalsLabel="GRAND TOTAL"
          sectionTitle="Donor Leaderboard"
          colorScheme={layout.colorScheme}
        />
      </>
    );
  };

  const renderTransactionsPreview = () => {
    const rows = (reportData.rows || []) as any[];
    const pg = reportData.pagination || { page: 1, totalPages: 1, total: 0 };
    const summaryItems = [
      { label: 'Total Transactions', value: reportData.summary?.totalTransactions || 0 },
      { label: 'Shown', value: reportData.summary?.shownTransactions || 0 },
      { label: 'Total Amount', value: fmtCurrency(reportData.summary?.grandTotal || 0), color: 'text-emerald-600 dark:text-emerald-400' },
      { label: 'Average', value: fmtCurrency(reportData.summary?.averageAmount || 0) },
    ];
    return (
      <>
        <ReportSummary items={summaryItems} />
        <ReportTable
          columns={[
            { label: 'Receipt #', key: 'receiptId', align: 'right' as const },
            { label: 'Date', key: 'dateStr' },
            { label: 'Donor ID', key: 'donorId' },
            { label: 'Donor Name', key: 'donorName' },
            { label: 'Type', key: 'donationType' },
            { label: 'Office', key: 'officeName' },
            { label: 'Cashier', key: 'cashierName' },
            { label: 'Amount', key: 'amount', isCurrency: true, align: 'right' as const },
          ]}
          rows={rows}
          showTotals totalsLabel="SUBTOTAL"
          sectionTitle={`Transactions (page ${pg.page} of ${pg.totalPages || 1})`}
          colorScheme={layout.colorScheme}
        />
        {pg.totalPages > 1 && (
          <div className="flex items-center justify-between text-sm mt-3 px-2">
            <span className="text-gray-500">
              Page <strong>{pg.page}</strong> of {pg.totalPages} · {pg.total.toLocaleString()} total
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary"
                disabled={pg.page <= 1 || loading}
                onClick={() => { setTxnPage(p => Math.max(1, p - 1)); setTimeout(handleGenerate, 0); }}>
                Previous
              </Button>
              <Button size="sm" variant="secondary"
                disabled={pg.page >= pg.totalPages || loading}
                onClick={() => { setTxnPage(p => p + 1); setTimeout(handleGenerate, 0); }}>
                Next
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  const buildPreview = () => {
    if (!reportData) return null;
    const rt = reportData.reportType;
    const hdr = reportData.header || {};
    const headerBlock = (
      <ReportHeader
        reportTitle={hdr.reportTitle || 'Donation Report'}
        reportSubtitle={hdr.reportSubtitle}
        dateRange={hdr.dateRange || { from: common.startDate, to: common.endDate }}
        donationTypes={hdr.donationTypes || []}
        colorScheme={layout.colorScheme}
      />
    );

    let body: React.ReactNode;
    if (rt === 'singleDonor') body = renderDonorPreview();
    else if (rt === 'byDonationType') body = renderTypePreview();
    else if (rt === 'byDonationTypeByRegion') body = renderRegionalTypePreview();
    else if (rt === 'byCashier') body = renderCashierPreview();
    else if (rt === 'topDonors') body = renderTopDonorsPreview();
    else if (rt === 'transactions') body = renderTransactionsPreview();
    else body = renderRegionOfficePreview();

    return (
      <div>
        {headerBlock}
        {body}
      </div>
    );
  };

  const donationTypeOptions = (donationTypes as any[]).map((t: any) => ({
    id: String(t._id),
    name: t.name,
  }));
  const cashierOptions = cashiers.map((c) => ({ id: c._id, name: c.name }));

  const scopePicker = (
    <div className="space-y-3">
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        Scope (optional)
      </label>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-2">
        Limit results to specific regions or offices. Leave empty for all.
      </p>
      <OfficeRegionFilter
        regions={regionsData}
        offices={officesData}
        selectedRegionIds={scopeRegionIds}
        selectedOfficeIds={scopeOfficeIds}
        onChangeRegions={setScopeRegionIds}
        onChangeOffices={setScopeOfficeIds}
      />
    </div>
  );

  // ─── Render ───────────────────────────────────────────
  return (
    <div className="space-y-6 pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
            <Receipt className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Donation Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Region, office, fund type, cashier, donor, and transaction reports
            </p>
          </div>
        </div>
        {onBack && <ReportCategoryBackButton onBack={onBack} />}
      </div>

      {/* Configuration Panel — sidebar tabs (matches Income/Expense layout) */}
      <div className="card border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-3 p-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 space-y-2 max-h-[520px] lg:max-h-none overflow-y-auto">
            {TABS.map((t) => (
              <TabButton
                key={t.id}
                active={activeTab === t.id}
                icon={t.icon}
                label={t.label}
                description={t.description}
                onClick={() => switchTab(t.id)}
              />
            ))}
          </div>

          <div className="lg:col-span-9 p-5 space-y-5">
          {/* Tab-specific controls */}
          {activeTab === 'regions' && (
            <div className="space-y-3">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Report Scope</label>
              <div className="flex gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" className="accent-primary-600" checked={regionMode === 'all'} onChange={() => { setRegionMode('all'); setSelectedRegionIds([]); }} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">All Regions</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" className="accent-primary-600" checked={regionMode === 'selected'} onChange={() => setRegionMode('selected')} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Specific Regions</span>
                </label>
              </div>
              {regionMode === 'selected' && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                  {(regionsData as any[]).map((r: any) => (
                    <label key={r._id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input type="checkbox" className="rounded border-gray-300 accent-primary-600"
                        checked={selectedRegionIds.includes(r._id)}
                        onChange={() => toggleItem(r._id, selectedRegionIds, setSelectedRegionIds)} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium text-primary-700 dark:text-primary-400">R-{r.regionId}</span> — {r.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {regionMode === 'selected' && (
                <p className="text-xs text-gray-400">
                  {selectedRegionIds.length} region(s) selected
                  {selectedRegionIds.length === 1 && ' — lists all offices in the region with per-fund breakdown'}
                </p>
              )}
            </div>
          )}

          {activeTab === 'offices' && (
            <div className="space-y-3">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Report Scope</label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: 'all', label: 'All Offices' },
                  { value: 'byRegion', label: 'Filter by Region' },
                  { value: 'selected', label: 'Select Offices' },
                ].map(opt => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" className="accent-primary-600" checked={officeMode === opt.value as any} onChange={() => { setOfficeMode(opt.value as any); setSelectedOfficeIds([]); setOfficeFilterRegionId(''); }} />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{opt.label}</span>
                  </label>
                ))}
              </div>
              {officeMode === 'byRegion' && (
                <select className="form-input text-sm w-full max-w-xs" value={officeFilterRegionId} onChange={e => setOfficeFilterRegionId(e.target.value)}>
                  <option value="">— All Regions —</option>
                  {(regionsData as any[]).map((r: any) => <option key={r._id} value={r._id}>R-{r.regionId} — {r.name}</option>)}
                </select>
              )}
              {officeMode === 'selected' && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                  {(officesData as any[]).map((o: any) => (
                    <label key={o._id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <input type="checkbox" className="rounded border-gray-300 accent-primary-600"
                        checked={selectedOfficeIds.includes(o._id)}
                        onChange={() => toggleItem(o._id, selectedOfficeIds, setSelectedOfficeIds)} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium text-primary-700 dark:text-primary-400">#{o.officeNo}</span> — {o.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {officeMode === 'selected' && <p className="text-xs text-gray-400">{selectedOfficeIds.length} office(s) selected</p>}
              {officeMode === 'byRegion' && officeFilterRegionId && (
                <p className="text-xs text-gray-400">Compares all offices in the selected region (ranked by total).</p>
              )}
            </div>
          )}

          {activeTab === 'types' && (
            <div className="space-y-3">
              {scopePicker}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 accent-primary-600"
                  checked={fullOffices}
                  onChange={e => setFullOffices(e.target.checked)}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  All offices per fund type (remove top-25 limit)
                </span>
              </label>
            </div>
          )}
          {activeTab === 'typesRegional' && scopePicker}
          {activeTab === 'cashiers' && scopePicker}

          {activeTab === 'topDonors' && (
            <div className="space-y-4">
              {scopePicker}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Show top:</label>
                <div className="flex gap-1.5">
                  {[10, 25, 50, 100, 200].map(n => (
                    <button
                      key={n}
                      onClick={() => setTopDonorsLimit(n)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                        ${topDonorsLimit === n
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-400'}`}
                    >Top {n}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {scopePicker}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Page size:</label>
                <div className="flex gap-1.5">
                  {[100, 200, 500, 1000].map(n => (
                    <button
                      key={n}
                      onClick={() => { setTxnLimit(n); setTxnPage(1); }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors
                        ${txnLimit === n
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-400'}`}
                    >{n}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'donor' && (
            <div className="space-y-3">
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Donor Lookup</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="form-input text-sm flex-1 max-w-xs uppercase"
                  placeholder="e.g. D001"
                  value={donorIdInput}
                  onChange={e => { setDonorIdInput(e.target.value.toUpperCase()); setLookedUpDonor(null); setLookupError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleDonorLookup(); }}
                />
                <Button size="sm" variant="secondary" onClick={handleDonorLookup} disabled={lookupLoading}
                  leftIcon={lookupLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}>
                  {lookupLoading ? 'Searching...' : 'Look Up'}
                </Button>
              </div>

              {lookupError && (
                <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-sm text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" /> {lookupError}
                </div>
              )}

              {lookedUpDonor && (
                <div className="p-4 rounded-xl border-2 border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                      <User className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">Donor Verified</span>
                    <span className="ml-auto text-xs font-mono bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">{lookedUpDonor.donorId}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div><span className="text-xs text-gray-400">Name</span><p className="font-medium text-gray-800 dark:text-gray-200">{lookedUpDonor.name}</p></div>
                    <div><span className="text-xs text-gray-400">{lookedUpDonor.relation}</span><p className="font-medium text-gray-800 dark:text-gray-200">{lookedUpDonor.fatherOrHusbandName}</p></div>
                    <div><span className="text-xs text-gray-400">CNIC</span><p className="font-medium font-mono text-gray-800 dark:text-gray-200">{lookedUpDonor.cnic || '—'}</p></div>
                    {lookedUpDonor.office && <div><span className="text-xs text-gray-400">Office</span><p className="font-medium text-gray-800 dark:text-gray-200">#{lookedUpDonor.office.officeNo} — {lookedUpDonor.office.name}</p></div>}
                  </div>
                </div>
              )}
            </div>
          )}

            <DateRangePicker
              startDate={common.startDate}
              endDate={common.endDate}
              onStartDateChange={(v) => setCommon((c) => ({ ...c, startDate: v }))}
              onEndDateChange={(v) => setCommon((c) => ({ ...c, endDate: v }))}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MultiSelect
                label="Fund types (optional)"
                options={donationTypeOptions}
                selected={common.donationTypeIds}
                onChange={(ids) => setCommon((c) => ({ ...c, donationTypeIds: ids }))}
              />
              <MultiSelect
                label="Cashiers (optional)"
                options={cashierOptions}
                selected={common.cashierIds}
                onChange={(ids) => setCommon((c) => ({ ...c, cashierIds: ids }))}
              />
            </div>
            {cashiersLoading && (
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading cashiers…
              </p>
            )}

            {activeTab === 'transactions' && (
              <div className="flex gap-4 items-end">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Limit</label>
                  <input
                    type="number"
                    min={1}
                    max={5000}
                    value={txnLimit}
                    onChange={(e) => { setTxnLimit(Number(e.target.value)); setTxnPage(1); }}
                    className="px-3 py-2 border rounded-lg w-24 dark:bg-gray-800 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Page</label>
                  <input
                    type="number"
                    min={1}
                    value={txnPage}
                    onChange={(e) => setTxnPage(Number(e.target.value))}
                    className="px-3 py-2 border rounded-lg w-24 dark:bg-gray-800 dark:border-gray-600"
                  />
                </div>
              </div>
            )}

            <LayoutConfigurator value={layout} onChange={setLayout} />

            {error && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleGenerate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                Generate
              </Button>
              <Button variant="secondary" onClick={handleExportExcel} disabled={exporting || !reportData}>
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button variant="secondary" onClick={handleExportPdf} disabled={exporting || !reportData}>
                PDF
              </Button>
              <Button variant="secondary" onClick={handlePrint} disabled={!reportData}>
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
              {reportData && (
                <Button variant="ghost" size="sm" onClick={() => { setReportData(null); setError(null); }}>
                  <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Reset
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {reportData && (
        <div className="card overflow-hidden">
          <div ref={printRef} className="p-6">{buildPreview()}</div>
        </div>
      )}
    </div>
  );
};

export default DonationReportsPanel;
